import { StandardEvents } from '@shopify/events';

// AllTerra Central — variant-reactive metafield region (<at-variant-reactive>)
//
// Every AllTerra PDP block that reads a metafield resolves it as
// "active variant value, else product value" (Product Architecture ticket).
// Those values are NOT in the variant JSON, so they can only be resolved
// server-side — which means the markup has to be re-rendered when the shopper
// picks another variant.
//
// Horizon already does the fetch: on `shopify:product:select` the variant
// picker requests the whole product page for the newly selected option values
// and hands listeners the parsed document on `event.promise` (see
// assets/variant-picker.js). It only morphs the picker itself, so anything else
// has to opt in. This component is that opt-in: wrap a block's output in
//
//   <at-variant-reactive data-key="..." data-product-id="...">…</at-variant-reactive>
//
// and its contents are swapped for the same wrapper's contents in the freshly
// rendered page. The wrapper is `display: contents` (at-variant-reactive.css),
// so it never affects layout, and it renders even when the block outputs
// nothing — an empty wrapper is the anchor that lets an empty block fill in
// (and a filled block empty out) on the next variant.
class AtVariantReactive extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.key) return;
    this.onSelect = this.onSelect.bind(this);
    // Listen on the document: PDP blocks live in the product section, but the
    // Product Details Tabs are a sibling section the event never reaches.
    document.addEventListener(StandardEvents.productSelect, this.onSelect);
  }

  disconnectedCallback() {
    if (this.onSelect) document.removeEventListener(StandardEvents.productSelect, this.onSelect);
  }

  async onSelect(event) {
    // Product cards and quick-add render a different product in their own
    // markup — their picker fetches a card section, not this page.
    const origin = event.target;
    if (origin instanceof Element && (origin.closest('product-card') || origin.closest('quick-add-dialog'))) return;

    let detail;
    try {
      ({ detail } = await event.promise);
    } catch {
      // Unavailable option combination (or aborted fetch) — keep what's shown.
      return;
    }

    const html = detail?.html;
    if (!html) return;

    // Another product's picker on the same page (e.g. a featured product block).
    const productId = this.dataset.productId;
    if (productId && detail.productId && detail.productId !== productId) return;

    const next = html.querySelector(`at-variant-reactive[data-key="${CSS.escape(this.dataset.key)}"]`);
    // No match means the block rendered nothing for the new variant, which is a
    // legitimate result (metafield empty on both variant and product) — clear.
    this.innerHTML = next ? next.innerHTML : '';
  }
}

if (!customElements.get('at-variant-reactive')) {
  customElements.define('at-variant-reactive', AtVariantReactive);
}
